// src/lib/pricing-force-resync-flow.js — FORCE RESYNC : ce que l'écran doit
// dire AVANT d'écraser un prix.
//
// Fonction pure, calquée sur invoice-avoir-flow.js / avoir-send-flow.js : elle
// ne lit rien, n'écrit rien, n'appelle rien. Elle rend les FAITS que la garde
// affiche, et le refus quand elle ne peut pas les établir.
//
// Ce que le geste fait, vérifié dans lib/pricing-handlers.js
// (handlePricingForceResync) :
//   - rate-limit 5 min, puis lecture Beds24 sur [aujourd'hui, +540 j] ;
//   - UN SEUL UPSERT sur `daily_pricing` (onConflict logement_id,date) portant
//     `price1, num_avail, min_stay, source:'manual_admin_force_resync',
//     fetched_at` — et RIEN d'autre ;
//   - une ligne d'audit SANS aucune valeur d'avant.
//   - AUCUN push vers Beds24 : c'est une lecture là-bas, une écriture ici.
//
// Deux conséquences que l'écran doit dire, parce que rien d'autre ne les dit :
//
//   (1) L'ÉPINGLE NE PROTÈGE PAS. Le trigger DB
//       `enforce_pricing_mode_write_protection` neutralise `cron_beds24_read`
//       et refuse `base_price_apply` ; `manual_admin_force_resync` est en
//       whitelist. Le prix épinglé est donc remplacé par celui de Beds24.
//
//   (2) DONC L'ÉPINGLE EST RETIRÉE AU PASSAGE (décision Dimitri, chemin
//       argent) : le payload porte `is_manual_override: false`. Sans cela, la
//       date resterait marquée 📌 sur un prix venu de Beds24 — et la branche
//       (1) du trigger (« le verrou ne s'auto-annule jamais ») interdirait au
//       cron de jamais le rafraîchir. Le prix serait figé définitivement,
//       affiché comme celui de Dimitri, et le verdict `conflict_locked` lui
//       attribuerait un écart sur un prix qu'il n'a jamais fixé. Ce n'est pas
//       un silence, c'est une fausse attribution durable.
//
// Et une troisième, désormais AVEC remède : le geste ne conservait rien — pas
// d'historique, pas de table d'avant, pas de retour en arrière. Le handler
// relève maintenant les prix épinglés AVANT l'upsert et les range dans le
// payload d'audit (`prix_epingles_avant`) : ils sont retrouvables.
//
// Enfin, ce que retirer l'épingle DÉVERROUILLE, et qui finit chez Beds24 :
//   · `lib/pricing-handlers.js` (push `base_price_apply`) exclut les dates 📌 —
//     après le resync, le prochain « appliquer le prix de base » POUSSE un prix
//     chez Beds24, donc sur les plateformes ;
//   · `lib/beds24-autoresolve.js` pose `skip("night_pinned")` sur les dates 📌,
//     décrit dans son propre en-tête comme « LA barrière anti-surbooking » —
//     après le resync, l'auto-résolution peut lever un blocage FBZ et rouvrir
//     la nuit à la réservation.
// Le geste lui-même ne pousse rien. Son lendemain, si.
//
// ⚠️ Le nombre de dates épinglées est un MAJORANT : l'upsert n'écrit que les
// dates que Beds24 renvoie. On ne promet donc jamais « exactement N ».
//
// ⚠️ `epinglesLoaded === false` ⇒ refus. Jamais `epinglesCount ?? 0` : un
// compte inconnu rendu « 0 date épinglée » est précisément le mensonge qu'on
// refuse — l'écran dirait « rien à perdre » au moment où il ne sait pas.

/**
 * Les faits à annoncer avant un force resync. Lecture pure.
 *
 * @param {object} args
 * @param {string}  args.logementId
 * @param {string}  [args.pricingMode]   - 'manual' | 'pricelabs' (informatif)
 * @param {number|null} [args.epinglesCount] - dates 📌 sur l'horizon
 * @param {boolean} [args.epinglesLoaded]  - false ⇒ compte indisponible
 * @param {number}  [args.horizonDays]     - 540
 * @param {string}  [args.dateFrom]        - ISO aaaa-mm-jj
 * @param {string}  [args.dateTo]          - ISO aaaa-mm-jj
 * @returns {{ possible: boolean, raison: string|null, faits: string[] }}
 */
export function describeForceResync({
  logementId,
  pricingMode,
  epinglesCount = null,
  epinglesLoaded = false,
  horizonDays = 540,
  dateFrom = null,
  dateTo = null,
} = {}) {
  if (!logementId || typeof logementId !== "string") {
    return {
      possible: false,
      raison: "Aucun logement désigné : ce geste ne peut pas partir.",
      faits: [],
    };
  }
  if (!epinglesLoaded || typeof epinglesCount !== "number") {
    return {
      possible: false,
      raison:
        "Je n'ai pas pu compter les dates épinglées pour ce logement. Ce geste écrase les prix fixés à la main et je ne peux pas te dire combien — vérifie le calendrier avant, ou réessaie.",
      faits: [],
    };
  }

  const periode =
    dateFrom && dateTo
      ? `Période relue : du ${formatJour(dateFrom)} au ${formatJour(dateTo)} (${horizonDays} jours).`
      : `Période relue : à partir d'aujourd'hui, ${horizonDays} jours.`;

  const faits = [periode];

  // Mode PriceLabs : le geste est le SEUL outil de relecture, et le risque 📌
  // y est nul (les prix viennent de PriceLabs, pas de la main de Dimitri).
  // On ne l'interdit pas — le trigger DB l'autorise explicitement pour ce mode.
  if (pricingMode === "pricelabs") {
    faits.push(
      "Ce logement est piloté par PriceLabs : ses prix viennent de là, pas de ta main.",
    );
  }

  if (epinglesCount > 0) {
    const s = epinglesCount > 1 ? "s" : "";
    faits.push(
      `${epinglesCount} date${s} ${epinglesCount > 1 ? "sont" : "est"} épinglée${s} 📌 sur cette période. Le verrou 📌 n'arrête pas ce geste (il n'arrête que le cron et le prix de base) : ${epinglesCount > 1 ? "leur prix sera remplacé" : "son prix sera remplacé"} par celui de Beds24, pour chacune des dates que Beds24 renvoie.`,
    );
    faits.push(
      `${epinglesCount > 1 ? "Ces épingles 📌 seront retirées" : "Cette épingle 📌 sera retirée"} au passage. Sans ça, un prix venu de Beds24 resterait marqué comme fixé par toi — et le robot de synchronisation ne pourrait plus jamais le rafraîchir.`,
    );
  } else {
    faits.push(
      "Aucune date épinglée sur cette période : aucun prix fixé à la main ne sera perdu.",
    );
  }

  faits.push(
    "Les autres dates (prix automatiques, disponibilité, séjour minimum) sont rafraîchies depuis Beds24.",
  );
  faits.push(
    epinglesCount > 0
      ? "Les prix épinglés sont relevés dans le journal d'audit juste avant d'être écrasés : tu pourras les relire et les reposer à la main. Nulle part ailleurs ils ne sont conservés."
      : "Rien à relever ici : aucun prix fixé à la main sur cette période.",
  );
  faits.push("Ce geste lui-même n'envoie rien à Beds24 : il ne fait que lire.");

  // MAIS son lendemain, si. Retirer l'épingle retire DEUX fail-closed qui,
  // eux, finissent chez Beds24 — donc sur les plateformes. Une garde dont la
  // promesse est « lis ce que tu perds » doit nommer ce qu'elle DÉVERROUILLE.
  if (epinglesCount > 0) {
    faits.push(
      "Ensuite, ces dates redeviennent ordinaires — et deux protections tombent avec l'épingle. (1) « Appliquer le prix de base » les évitait : au prochain envoi, un prix partira chez Beds24, donc sur les plateformes, sur des nuits que tu en avais sorties.",
    );
    faits.push(
      "(2) La résolution automatique des écarts Beds24 les évitait aussi — c'est la barrière anti-surbooking. Sans épingle, elle peut lever un blocage et rouvrir la nuit à la réservation.",
    );
  }

  return { possible: true, raison: null, faits };
}

/**
 * Le bouton de confirmation part-il ? Une seule source pour la garde de
 * l'écran : tant que `describeForceResync` n'a pas pu établir ses faits
 * (compte des 📌 non revenu, ou illisible), le bouton reste désactivé.
 *
 * Fonction séparée pour que la règle soit testable sans monter tout l'écran :
 * la retirer côté JSX doit rougir ici ET dans le test structurel du panneau.
 *
 * @param {{possible?: boolean}|null} desc — retour de describeForceResync
 * @returns {boolean} true ⇒ bouton grisé
 */
export function forceResyncOkDisabled(desc) {
  return !desc || desc.possible !== true;
}

/** aaaa-mm-jj → jj/mm/aaaa. Rend la chaîne telle quelle si elle n'a pas la forme. */
function formatJour(iso) {
  if (typeof iso !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const [a, m, j] = iso.split("-");
  return `${j}/${m}/${a}`;
}

export default describeForceResync;
